import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Cpu, HardDrive, Monitor, Package, Zap, Building2, BarChart3 } from 'lucide-react';
import SEO from '../components/SEO';

export default function ITHardwareSolutions() {
  const canonical = 'https://divaisetech.com/it-hardware-solutions';
  const servicePageSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Enterprise IT Hardware Solutions',
    description: 'End-to-end IT hardware procurement, fulfillment, and deployment services for enterprise infrastructure.',
    url: canonical,
    provider: {
      '@type': 'Organization',
      name: 'DivaiseTech',
      url: 'https://divaisetech.com',
      logo: 'https://divaisetech.com/logo.png',
    },
    areaServed: {
      '@type': 'Place',
      name: 'Worldwide',
    },
    serviceType: 'IT Hardware Procurement and Fulfillment',
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
    },
  };

  const hardwareSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Enterprise IT Hardware Procurement & Fulfillment Services',
      description: 'End-to-end IT hardware procurement, fulfillment, and deployment for data centers, AI infrastructure, and enterprise computing.',
      provider: {
        '@type': 'Organization',
        name: 'Divaise Technology Private Limited',
        url: 'https://divaisetech.com',
      },
      serviceType: 'IT Hardware Procurement & Data Center Solutions',
      areaServed: {
        '@type': 'Place',
        name: 'Worldwide',
      },
    },
  ];

  const services = [
    {
      icon: Cpu,
      title: 'AI & GPU Hardware Procurement',
      description: 'Specialized sourcing of AI accelerators, GPUs, and compute chips.',
      details: [
        'NVIDIA GPU selection (H100, A100, RTX series optimization)',
        'AMD EPYC processors for data centers',
        'Tensor processing units and specialized accelerators',
        'ML training cluster hardware planning',
        'Cost optimization across regions',
      ],
    },
    {
      icon: HardDrive,
      title: 'Data Center Hardware Supply',
      description: 'Complete data center infrastructure including servers, storage, and networking.',
      details: [
        'Enterprise-grade servers (Dell, HPE, Supermicro)',
        'High-capacity SSD/NVMe storage systems',
        'Switches, routers, and networking gear',
        'Power and cooling infrastructure',
        'Redundancy & failover components',
      ],
    },
    {
      icon: Monitor,
      title: 'Enterprise Computing Solutions',
      description: 'Workstations, laptops, and edge computing devices.',
      details: [
        'High-performance workstations for engineering',
        'Secure business laptops and ultrabooks',
        'Mobile devices and tablets',
        'IoT edge gateways and appliances',
        'Ruggedized devices for field operations',
      ],
    },
    {
      icon: Package,
      title: 'Peripheral & Infrastructure Equipment',
      description: 'Printers, CCTV, networking, and facility infrastructure.',
      details: [
        'Multifunction printers and enterprise copiers',
        'CCTV cameras and surveillance systems',
        'Network cables, racks, and PDUs',
        'Backup power (UPS) systems',
        'Environmental monitoring equipment',
      ],
    },
  ];

  const faqs = [
    {
      question: 'How do you source hardware at competitive prices?',
      answer: 'We maintain relationships with tier-1 vendors (Dell, HP, Lenovo, NVIDIA, etc.) and leverage volume agreements. We also source globally to find the best pricing while ensuring warranty and support.',
    },
    {
      question: 'What about hardware compatibility and testing?',
      answer: 'Before fulfillment, we validate hardware against your specifications, perform compatibility checks, run diagnostic tests, and create detailed documentation.',
    },
    {
      question: 'Do you handle installation and setup?',
      answer: 'Yes. We offer on-site installation, configuration, integration with your existing infrastructure, OS/firmware installation, and testing.',
    },
    {
      question: 'What warranty and support options are available?',
      answer: 'We arrange extended warranties, on-site support contracts, and maintenance plans. We negotiate terms with vendors on your behalf.',
    },
    {
      question: 'Can you help with hardware lifecycle management?',
      answer: 'Yes. We provide end-of-life assessments, secure disposal/recycling, trade-in evaluations, and migration planning for infrastructure upgrades.',
    },
  ];

  return (
    <div>
      <SEO
        title="IT Hardware Procurement & Fulfillment Services"
        description="Enterprise IT hardware procurement, fulfillment, and deployment. GPU servers, laptops, CCTV, printers, networking gear, and data center equipment."
        keywords="IT hardware procurement, enterprise hardware supplier, GPU hardware, data center equipment, laptop procurement, CCTV systems, printer solutions"
        canonical={canonical}
        schema={[...hardwareSchema, servicePageSchema]}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 -left-40 w-80 h-80 bg-accent rounded-full mix-blend-multiply filter blur-3xl"></div>
          <div className="absolute top-40 -right-40 w-80 h-80 bg-secondary rounded-full mix-blend-multiply filter blur-3xl"></div>
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="mb-12">
            <span className="inline-block bg-accent/20 text-accent px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Hardware & Infrastructure Solutions
            </span>
            <h1 className="text-5xl md:text-6xl font-bold mb-8 leading-tight">
              Enterprise IT Hardware Procurement & Fulfillment
            </h1>
            <p className="text-xl text-slate-200 max-w-3xl mb-8 leading-relaxed">
              Streamlined sourcing, procurement, and deployment of IT hardware from laptops to data center infrastructure. GPU servers, CCTV systems, printers, networking equipment, and more—delivered on time, on budget, with full integration support.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-16">
            <Link
              to="/request-hardware-quote"
              className="inline-flex items-center bg-accent text-black px-8 py-4 rounded-lg font-semibold hover:bg-accent/90 transition-all hover:shadow-lg"
            >
              Request Hardware Quote <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center border-2 border-secondary text-white px-8 py-4 rounded-lg font-semibold hover:bg-secondary/20 transition-all"
            >
              Talk to Hardware Specialist
            </Link>
          </div>

          {/* Key Stats */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-lg p-6">
              <div className="text-3xl font-bold text-accent mb-2">500+</div>
              <p className="text-slate-200">Hardware Procurement Projects</p>
            </div>
            <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-lg p-6">
              <div className="text-3xl font-bold text-accent mb-2">$100M+</div>
              <p className="text-slate-200">Hardware Deployed</p>
            </div>
            <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-lg p-6">
              <div className="text-3xl font-bold text-accent mb-2">30+ countries</div>
              <p className="text-slate-200">Global Fulfillment Coverage</p>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 text-white">End-to-End Hardware Solutions</h2>
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                From AI chips and server farms to laptops, printers, and CCTV systems—we handle every aspect of enterprise hardware procurement. Our team negotiates with top-tier vendors globally to secure the best pricing, shortest lead times, and full warranty coverage.
              </p>
              <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                Whether you're building a new data center, upgrading your compute infrastructure for AI workloads, or standardizing on new laptops and office equipment, we deliver a friction-free procurement experience backed by expert support.
              </p>
            </div>
            <div className="bg-slate-950 rounded-xl border border-white/10 p-8">
              <h3 className="text-xl font-bold mb-6 text-white">Our Hardware Coverage:</h3>
              <ul className="space-y-3">
                {['GPUs & AI Accelerators', 'Enterprise Servers', 'Storage Systems', 'Networking Equipment', 'Workstations & Laptops', 'Printers & MFPs', 'CCTV & Security', 'Edge & IoT Devices'].map((item, idx) => (
                  <li key={idx} className="flex gap-3 items-start">
                    <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-4 text-center text-white">Hardware Procurement Services</h2>
          <p className="text-xl text-slate-300 text-center mb-16 max-w-2xl mx-auto">
            Complete sourcing, procurement, integration, and support for every hardware category.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((svc, idx) => {
              const Icon = svc.icon;
              return (
                <div key={idx} className="bg-slate-950 border border-white/10 rounded-xl p-8 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <Icon className="w-8 h-8 text-accent flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-3 text-white">{svc.title}</h3>
                      <p className="text-slate-300 mb-4">{svc.description}</p>
                      <ul className="space-y-2">
                        {svc.details.map((detail, didx) => (
                          <li key={didx} className="text-sm text-slate-300 flex gap-2">
                            <span className="text-accent font-bold">•</span> {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Hardware Categories Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-16 text-center text-white">Hardware Categories & Specialties</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-slate-950 rounded-xl border border-white/10 p-8">
              <h3 className="text-xl font-bold mb-6 text-white">AI & Compute Hardware</h3>
              <ul className="space-y-2 text-slate-300">
                <li>• NVIDIA CUDA GPUs (H100, A100, RTX)</li>
                <li>• AMD EPYC Processors</li>
                <li>• Intel Xeon Scalable CPUs</li>
                <li>• TPU and Tensor Processing Units</li>
                <li>• High-Memory Server Configurations</li>
                <li>• NVMe & FastStorage Systems</li>
              </ul>
            </div>
            <div className="bg-slate-950 rounded-xl border border-white/10 p-8">
              <h3 className="text-xl font-bold mb-6 text-white">Office & Endpoint Devices</h3>
              <ul className="space-y-2 text-slate-300">
                <li>• Business Laptops (Dell, HP, Lenovo)</li>
                <li>• High-Performance Workstations</li>
                <li>• Desktop Computers & All-in-Ones</li>
                <li>• Network Printers & Multifunction Devices</li>
                <li>• Tablets & Mobile Devices</li>
                <li>• Video Conferencing Equipment</li>
              </ul>
            </div>
            <div className="bg-slate-950 rounded-xl border border-white/10 p-8">
              <h3 className="text-xl font-bold mb-6 text-white">Infrastructure & Security</h3>
              <ul className="space-y-2 text-slate-300">
                <li>• Network Switches & Routers</li>
                <li>• Cabling & Connectivity Systems</li>
                <li>• CCTV Cameras & Surveillance Systems</li>
                <li>• Access Control & Security Appliances</li>
                <li>• Power Distribution & UPS Systems</li>
                <li>• Environmental Monitoring Equipment</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Vendor Partners Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-16 text-center text-white">Trusted Vendor Partnerships</h2>
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {['Dell Technologies', 'Hewlett Packard Enterprise', 'Lenovo Enterprise', 'NVIDIA', 'AMD', 'Intel', 'Cisco Systems', 'NetApp', 'Super Micro', 'Juniper Networks', 'Arista Networks', 'F5 Networks'].map((vendor, idx) => (
              <div key={idx} className="bg-slate-950 rounded-lg border border-white/10 p-6 text-center hover:shadow-lg transition-shadow">
                <p className="font-semibold text-white">{vendor}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-slate-300">
            We partner with 50+ hardware manufacturers and resellers to source equipment globally. This gives us access to inventory, competitive pricing, and dedicated vendor support.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-16 text-center text-white">Why Choose Divaise for Hardware</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Vendor Negotiation & Cost Optimization',
                desc: 'We negotiate pricing, volume discounts, and warranty terms directly with manufacturers.',
              },
              {
                title: 'Global Fulfillment Network',
                desc: 'Access to worldwide inventory and logistics partners ensures on-time delivery to any location.',
              },
              {
                title: 'Technical Expertise & Configuration',
                desc: 'Our engineers validate hardware, optimize configurations, and ensure compatibility with your infrastructure.',
              },
              {
                title: 'Installation & Integration Support',
                desc: 'We handle on-site installation, configuration, OS deployment, and integration testing.',
              },
              {
                title: 'Warranty & Support Management',
                desc: 'We arrange extended warranties, on-site support contracts, and simplify vendor communication.',
              },
              {
                title: 'Lifecycle Management',
                desc: 'End-of-life planning, secure disposal, trade-in evaluations, and upgrade forecasting.',
              },
            ].map((benefit, idx) => (
              <div key={idx} className="bg-slate-950 rounded-lg p-8 border border-white/10">
                <h3 className="text-lg font-bold mb-2 text-white">{benefit.title}</h3>
                <p className="text-slate-300">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-16 text-center text-white">Hardware Procurement Process</h2>
          <div className="space-y-6">
            {[
              {
                step: '1. Requirements Assessment',
                details: 'Understand your hardware needs, performance requirements, budget, timeline, and constraints.',
              },
              {
                step: '2. Vendor & Product Selection',
                details: 'Recommend optimal hardware options, compare specs/pricing, negotiate terms with vendors.',
              },
              {
                step: '3. Purchase & Fulfillment',
                details: 'Place orders, track shipments, manage inventory, coordinate global logistics.',
              },
              {
                step: '4. Quality Assurance',
                details: 'Inspect upon arrival, perform diagnostics, verify specs, test functionality.',
              },
              {
                step: '5. Installation & Integration',
                details: 'On-site delivery, installation, rack/mount, firmware/OS setup, testing, documentation.',
              },
              {
                step: '6. Ongoing Support',
                details: 'Warranty management, technical support, maintenance contracts, future upgrades.',
              },
            ].map((item, idx) => (
              <div key={idx} className="bg-slate-950 border border-white/10 rounded-lg p-8 flex gap-6">
                <div className="bg-accent text-black rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0 font-bold">
                  {item.step.split('.')[0]}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-2">{item.step}</h3>
                  <p className="text-slate-300">{item.details}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-16 text-center text-white">Hardware Procurement Success Story</h2>
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-xl p-12">
            <h3 className="text-2xl font-bold mb-4">AI Data Center Build-Out: Global Tech Company</h3>
            <p className="text-slate-200 mb-6 leading-relaxed">
              A Fortune 500 tech company needed to procure 500 GPU servers (NVIDIA H100s) and associated infrastructure for a new AI research facility. The challenge: 12-week global procurement with strict performance requirements and budget constraints.
            </p>
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div>
                <div className="text-4xl font-bold text-accent mb-2">$15.2M</div>
                <p className="text-slate-200">Hardware deployment value</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-accent mb-2">12 weeks</div>
                <p className="text-slate-200">Full procurement & deployment</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-accent mb-2">18%</div>
                <p className="text-slate-200">Cost savings via negotiation</p>
              </div>
            </div>
            <Link to="/contact" className="inline-flex items-center bg-accent text-black px-6 py-3 rounded-lg font-semibold hover:bg-accent/90 transition-all">
              Discuss Your Hardware Project <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-bold mb-16 text-center text-white">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <details key={idx} className="group bg-slate-950 border border-white/10 rounded-lg p-6 cursor-pointer hover:shadow-lg transition-shadow">
                <summary className="flex justify-between items-center font-semibold text-white">
                  {faq.question}
                  <span className="text-accent">+</span>
                </summary>
                <p className="mt-4 text-slate-300 leading-relaxed">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-r from-accent to-accent/80 text-black">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Simplify Hardware Procurement?</h2>
          <p className="text-xl mb-8 text-black/80">
            Get a custom quote for your hardware needs. Our team responds within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/request-hardware-quote"
              className="inline-flex items-center bg-black text-white px-8 py-4 rounded-lg font-semibold hover:bg-black/90 transition-all"
            >
              Request Hardware Quote <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center border-2 border-black bg-transparent text-black px-8 py-4 rounded-lg font-semibold hover:bg-black/10 transition-all"
            >
              Contact Sales Team
            </Link>
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-16 px-6 bg-black border-t border-gray-800">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-lg font-bold text-white mb-6">Related Services</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/solutions" className="text-accent hover:underline font-semibold">
              View All Solutions →
            </Link>
            <Link to="/digital-experience-platform-solutions" className="text-accent hover:underline font-semibold">
              AEM Development Services →
            </Link>
            <Link to="/about" className="text-accent hover:underline font-semibold">
              Learn About Our Team →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
